import React, { Fragment, useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import { useGetPartnersMutation } from '../../../redux/features/user/partnersApiSlice';
import {
  useGetAllUsersByPartnerMutation,
  useSendUserPasswordMutation,
} from '../../../redux/features/user/usersApiSlice';
import { Dialog, Transition } from '@headlessui/react';
import useAuth from '../../../hooks/useAuth';

const SendUserPassword = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [getPartners] = useGetPartnersMutation();
  const [getAllUsers] = useGetAllUsersByPartnerMutation();
  const [sendUserPassword] = useSendUserPasswordMutation();
  const [partnersList, setPartnersList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [partnerId, setPartnerId] = useState(-1);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const userinfo = useAuth();

  useEffect(() => {
    const fetchPartners = async () => {
      const result = await getPartners('register-user').unwrap();
      setPartnersList(result);
    };

    fetchPartners();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      if (partnerId === -1) {
        setUsersList([]);
        return;
      }
      const result = await getAllUsers({
        partnerId: partnerId,
        userUuid: userinfo.uuid,
      }).unwrap();
      setUsersList(Array.isArray(result) ? result : []);
    };

    fetchUsers();
  }, [partnerId]);

  function closeModal() {
    setIsOpen(false);
    setStatusMessage(null);
    setStatusError(null);
    setPartnerId(-1);
    setUsersList([]);
  }

  function openModal() {
    setIsOpen(true);
  }

  const modalInitialValues = {
    User: '',
  };

  const handleSubmit = async (formData, { resetForm }) => {
    try {
      const result = await sendUserPassword(formData).unwrap();
      setStatusError(null);
      setStatusMessage(result);
      resetForm();
    } catch (error) {
      setStatusMessage(null);
      setStatusError(error?.data || 'Could not send password email');
    }
  };

  return (
    <div>
      <button
        onClick={openModal}
        type='button'
        className='bg-[#0ea5e9] hover:bg-sky-700 w-64 rounded-lg text-white py-2 text-center items-center'
      >
        Send user password
      </button>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as='div' className='relative z-10' onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black bg-opacity-25' />
          </Transition.Child>

          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-center justify-center p-4 text-center'>
              <Transition.Child
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'
              >
                <Dialog.Panel className='w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all'>
                  <Dialog.Title
                    as='h3'
                    className='text-lg font-medium leading-6 mb-2 text-gray-900'
                  >
                    Send new password by email
                  </Dialog.Title>
                  <p className='text-sm text-gray-500 mb-4'>
                    Select a partner and a user. A new password will be saved
                    and emailed to that user.
                  </p>
                  <div className='flex flex-col gap-4'>
                    <select
                      className='border border-[#B7C6D7] rounded-md w-64 px-2 py-2 text-black placeholder-[#B7C6D7]'
                      value={partnerId === -1 ? '' : partnerId}
                      onChange={(e) => setPartnerId(e.target.value)}
                    >
                      <option disabled value=''>
                        Select partner
                      </option>
                      {partnersList.map((partner) => (
                        <option key={partner.id} value={partner.id}>
                          {partner.name}
                        </option>
                      ))}
                    </select>
                    <Formik
                      enableReinitialize={true}
                      initialValues={modalInitialValues}
                      onSubmit={handleSubmit}
                    >
                      <Form className='flex flex-col sm:flex-row gap-4 sm:items-center'>
                        <Field
                          className='border border-[#B7C6D7] rounded-md w-64 px-2 py-2 text-black placeholder-[#B7C6D7]'
                          as='select'
                          name='User'
                        >
                          <option disabled value=''>
                            Select user
                          </option>
                          {usersList.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.username}
                            </option>
                          ))}
                        </Field>
                        <button
                          type='submit'
                          className='bg-[#0ea5e9] hover:bg-sky-700 px-4 rounded-lg text-white py-2 text-center items-center'
                        >
                          Send password
                        </button>
                      </Form>
                    </Formik>
                    {statusMessage && (
                      <p className='text-green-700'>{statusMessage}</p>
                    )}
                    {statusError && (
                      <p className='text-red-500'>{statusError}</p>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default SendUserPassword;
